import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// 저장하지 않은 입력이 있는 화면이 로그아웃 전에 확인을 받을 수 있게 하는 표시
let hasUnsavedChanges = false;

export const useUnsavedChangesGuard = (dirty: boolean) => {
  useEffect(() => {
    hasUnsavedChanges = dirty;
    return () => {
      hasUnsavedChanges = false;
    };
  }, [dirty]);
};

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, readOnly } = useAuth();

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    if (hasUnsavedChanges && !window.confirm('저장하지 않은 내용이 있어요. 나갈까요?')) {
      return;
    }
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {readOnly && (
        <div role="alert" className="bg-yellow-100 text-yellow-900 text-sm px-4 py-2 text-center">
          서버가 복구 중이라 읽기만 가능해요. 저장·수정·삭제는 할 수 없어요 ({readOnly})
        </div>
      )}
      {/* 헤더 */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center">
                  <span className="text-white text-sm font-bold">TS</span>
                </div>
                <span className="text-xl font-bold text-gray-900">TourStream Admin</span>
              </Link>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              로그아웃
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col xl:flex-row gap-4 xl:gap-6">
          {/* 사이드바 */}
          <aside className="w-full xl:w-56 2xl:w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg shadow-sm p-4">
              <ul className="space-y-1">
                <li>
                  <Link
                    to="/"
                    className={`block px-4 py-2 rounded-lg transition-colors text-sm ${
                      isActive('/')
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    대시보드
                  </Link>
                </li>
                <li className="pt-2">
                  <div className="px-4 py-2 text-base font-bold text-gray-700">상품 관리</div>
                </li>
                <li>
                  <Link
                    to="/products/new"
                    className={`block px-4 py-2 rounded-lg transition-colors text-sm ${
                      isActive('/products/new')
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    상품 등록
                  </Link>
                </li>
                <li>
                  <Link
                    to="/products"
                    className={`block px-4 py-2 rounded-lg transition-colors text-sm ${
                      isActive('/products')
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    상품 목록
                  </Link>
                </li>
                <li className="pt-2">
                  <div className="px-4 py-2 text-base font-bold text-gray-700">카테고리 관리</div>
                </li>
                <li>
                  <Link
                    to="/categories/new"
                    className={`block px-4 py-2 rounded-lg transition-colors text-sm ${
                      isActive('/categories/new')
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    카테고리 등록
                  </Link>
                </li>
                <li>
                  <Link
                    to="/categories"
                    className={`block px-4 py-2 rounded-lg transition-colors text-sm ${
                      isActive('/categories')
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    카테고리 목록
                  </Link>
                </li>
                <li className="pt-2">
                  <div className="px-4 py-2 text-base font-bold text-gray-700">지역 관리</div>
                </li>
                <li>
                  <Link
                    to="/locations/new"
                    className={`block px-4 py-2 rounded-lg transition-colors text-sm ${
                      isActive('/locations/new')
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    지역 등록
                  </Link>
                </li>
                <li>
                  <Link
                    to="/locations"
                    className={`block px-4 py-2 rounded-lg transition-colors text-sm ${
                      isActive('/locations')
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    지역 목록
                  </Link>
                </li>
              </ul>
            </nav>
          </aside>

          {/* 메인 컨텐츠 */}
          <main className="flex-1 min-w-0">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};
